// Type-only: `embed-content-contract.ts` imports values from this module, so a
// value import back would be a runtime cycle. The status vocabulary is declared
// there, beside the evidence contract that consumes it.
import type { CatalogEmbedContentStatus } from "./embed-content-contract.js";

export type DataCatalogEmbedKind = "object" | "fieldGroup" | "template";

/** Single source for the template vocabulary: the type below, the label map and
 *  the Convex validator are all derived from this array. */
export const DATA_CATALOG_TEMPLATE_KEYS = [
  "sourceSummary",
  "keyFields",
  "termSummary",
  "businessContext",
  "ownershipGovernance",
  "lineageSnapshot",
  "governanceStatus",
] as const;

export type DataCatalogTemplateKey = (typeof DATA_CATALOG_TEMPLATE_KEYS)[number];

export const DATA_CATALOG_TEMPLATE_LABELS: Record<DataCatalogTemplateKey, string> = {
  sourceSummary: "Source Summary",
  keyFields: "Key Fields",
  termSummary: "Term Summary",
  businessContext: "Business Context",
  ownershipGovernance: "Ownership & Governance",
  lineageSnapshot: "Lineage Snapshot",
  governanceStatus: "Governance Status",
};

export interface DataCatalogEntryAttrs {
  embedId: string;
  connectionId: string;
  catalogDisplayName: string;
  providerKey: string;
  objectStableRef: string;
  objectCategory: string;
  objectDisplayLabel: string;
  embedKind: DataCatalogEmbedKind;
  templateKey: DataCatalogTemplateKey | "";
  templateLabel: string;
  selectedFieldKeys: string[];
  selectedFieldLabels: string[];
  selectedFieldValues: string[];
  status: CatalogEmbedContentStatus;
  brokenReason: string;
  /** Semantically lastResolvedAt — only ever stamped when status resolves to "current". */
  lastSyncedAt: number | null;
  /** Last refresh attempt, any outcome (LAT-571). Null until the first refresh runs. */
  lastCheckedAt: number | null;
}

function normalizeTemplateKey(value: unknown): DataCatalogTemplateKey | "" {
  return typeof value === "string" &&
    (DATA_CATALOG_TEMPLATE_KEYS as readonly string[]).includes(value)
    ? (value as DataCatalogTemplateKey)
    : "";
}

function normalizeRenderableStrings(values: unknown): string[] {
  return Array.isArray(values)
    ? values.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

/**
 * The single source of truth for narrowing an arbitrary status value down to
 * the allowed set. Every parseHTML/renderHTML guard and persistence-path
 * status parser must go through this instead of hand-inlining the check —
 * a hand-inlined copy is exactly how "unverifiable" got silently dropped
 * back to "current" in one persistence path during the LAT-571 PR1 widen.
 */
export function normalizeCatalogEmbedStatus(value: unknown): CatalogEmbedContentStatus {
  return value === "stale" || value === "broken" || value === "unverifiable" ? value : "current";
}

export function catalogStatusBadgeLabel(status: DataCatalogEntryAttrs["status"]): string {
  switch (status) {
    case "current":
      return "Current";
    case "stale":
      return "Stale";
    case "broken":
      return "Broken";
    case "unverifiable":
      return "Unverifiable";
  }

  const exhaustiveStatus: never = status;
  return exhaustiveStatus;
}

export function normalizeDataCatalogEntryAttrs(
  attributes: Record<string, unknown>
): DataCatalogEntryAttrs {
  const selectedFieldKeys = Array.isArray(attributes.selectedFieldKeys)
    ? attributes.selectedFieldKeys.filter(
        (item): item is string => typeof item === "string" && item.length > 0
      )
    : [];
  const selectedFieldLabelsRaw = Array.isArray(attributes.selectedFieldLabels)
    ? attributes.selectedFieldLabels
    : [];
  const selectedFieldValuesRaw = Array.isArray(attributes.selectedFieldValues)
    ? attributes.selectedFieldValues
    : [];

  return {
    embedId: typeof attributes.embedId === "string" ? attributes.embedId : "",
    connectionId: typeof attributes.connectionId === "string" ? attributes.connectionId : "",
    catalogDisplayName:
      typeof attributes.catalogDisplayName === "string" ? attributes.catalogDisplayName : "",
    providerKey: typeof attributes.providerKey === "string" ? attributes.providerKey : "",
    objectStableRef:
      typeof attributes.objectStableRef === "string" ? attributes.objectStableRef : "",
    objectCategory: typeof attributes.objectCategory === "string" ? attributes.objectCategory : "",
    objectDisplayLabel:
      typeof attributes.objectDisplayLabel === "string" ? attributes.objectDisplayLabel : "",
    embedKind:
      attributes.embedKind === "fieldGroup"
        ? "fieldGroup"
        : attributes.embedKind === "template"
          ? "template"
          : "object",
    templateKey: normalizeTemplateKey(attributes.templateKey),
    templateLabel: typeof attributes.templateLabel === "string" ? attributes.templateLabel : "",
    selectedFieldKeys,
    selectedFieldLabels: selectedFieldKeys.map((_, index) =>
      typeof selectedFieldLabelsRaw[index] === "string" ? selectedFieldLabelsRaw[index] : ""
    ),
    selectedFieldValues: selectedFieldKeys.map((_, index) =>
      typeof selectedFieldValuesRaw[index] === "string" ? selectedFieldValuesRaw[index] : ""
    ),
    status: normalizeCatalogEmbedStatus(attributes.status),
    brokenReason: typeof attributes.brokenReason === "string" ? attributes.brokenReason : "",
    lastSyncedAt: typeof attributes.lastSyncedAt === "number" ? attributes.lastSyncedAt : null,
    lastCheckedAt: typeof attributes.lastCheckedAt === "number" ? attributes.lastCheckedAt : null,
  };
}

export function resolveFieldGroupRenderedLabel(labels: string[], values: string[]): string {
  if (values.length > 0) {
    return values.join(", ");
  }

  if (labels.length > 0) {
    return labels.join(", ");
  }

  return "";
}

export function resolveRenderedLabel(attributes: Record<string, unknown>): string {
  const embedKind =
    attributes.embedKind === "fieldGroup"
      ? "fieldGroup"
      : attributes.embedKind === "template"
        ? "template"
        : "object";
  if (embedKind === "fieldGroup") {
    const renderedFieldGroupLabel = resolveFieldGroupRenderedLabel(
      normalizeRenderableStrings(attributes.selectedFieldLabels),
      normalizeRenderableStrings(attributes.selectedFieldValues)
    );
    if (renderedFieldGroupLabel) {
      return renderedFieldGroupLabel;
    }
  }

  if (embedKind === "template") {
    const templateLabel =
      typeof attributes.templateLabel === "string" ? attributes.templateLabel.trim() : "";
    if (templateLabel.length > 0) {
      return templateLabel;
    }

    const templateKey = normalizeTemplateKey(attributes.templateKey);
    if (templateKey) {
      return DATA_CATALOG_TEMPLATE_LABELS[templateKey];
    }
  }

  const displayLabel =
    typeof attributes.objectDisplayLabel === "string" ? attributes.objectDisplayLabel.trim() : "";
  if (displayLabel.length > 0) {
    return displayLabel;
  }

  return typeof attributes.objectStableRef === "string" ? attributes.objectStableRef : "";
}

function renderCatalogStatusText(attrs: DataCatalogEntryAttrs): string | null {
  if (attrs.status === "broken") {
    const reason = attrs.brokenReason.trim();
    const label = catalogStatusBadgeLabel(attrs.status);
    return reason.length > 0 ? `${label} — ${reason}` : label;
  }
  if (attrs.status !== "current") {
    return catalogStatusBadgeLabel(attrs.status);
  }
  return null;
}

function renderCatalogMetadataParts(attrs: DataCatalogEntryAttrs): string[] {
  return [attrs.objectCategory, attrs.catalogDisplayName].filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0
  );
}

export function renderCatalogInlineText(attributes: Record<string, unknown>): string {
  const attrs = normalizeDataCatalogEntryAttrs(attributes);
  const label = resolveRenderedLabel(attributes).trim();
  if (!label) {
    return "";
  }

  const parts = [`Catalog reference: ${label}`];
  const metadata = renderCatalogMetadataParts(attrs);
  if (metadata.length > 0) {
    parts.push(metadata.join(" | "));
  }
  const status = renderCatalogStatusText(attrs);
  if (status) {
    parts.push(status);
  }
  return `[${parts.join(" | ")}]`;
}

export function renderCatalogTemplateMarkdown(attributes: Record<string, unknown>): string {
  const attrs = normalizeDataCatalogEntryAttrs(attributes);
  const title = resolveRenderedLabel(attributes).trim() || "Catalog Embed";
  const lines = [`**Catalog embed — ${title}**`];

  if (attrs.catalogDisplayName.trim().length > 0) {
    lines.push(`- Catalog: ${attrs.catalogDisplayName.trim()}`);
  }
  const objectLabel = attrs.objectDisplayLabel.trim() || attrs.objectStableRef.trim();
  if (objectLabel.length > 0) {
    lines.push(`- Object: ${objectLabel}`);
  }
  if (attrs.objectCategory.trim().length > 0) {
    lines.push(`- Type: ${attrs.objectCategory.trim()}`);
  }

  for (let index = 0; index < attrs.selectedFieldKeys.length; index += 1) {
    const label =
      attrs.selectedFieldLabels[index]?.trim() || attrs.selectedFieldKeys[index]?.trim();
    const value = attrs.selectedFieldValues[index]?.trim();
    if (!label && !value) {
      continue;
    }
    lines.push(value && value.length > 0 ? `- ${label}: ${value}` : `- ${label}`);
  }

  const status = renderCatalogStatusText(attrs);
  if (status) {
    lines.push(`- Refresh status: ${status}`);
  }

  return `${lines.join("\n")}\n\n`;
}

export function summarizeCatalogTemplate(attributes: Record<string, unknown>): string {
  const attrs = normalizeDataCatalogEntryAttrs(attributes);
  const title = resolveRenderedLabel(attributes).trim() || "Catalog embed";
  const objectLabel = attrs.objectDisplayLabel.trim() || attrs.objectStableRef.trim();
  const summaryParts = [title];
  if (objectLabel.length > 0 && objectLabel !== title) {
    summaryParts.push(objectLabel);
  }
  const status = renderCatalogStatusText(attrs);
  if (status) {
    summaryParts.push(status);
  }
  return summaryParts.join(" — ");
}
